import { Controller } from "@hotwired/stimulus";
import { Editor, Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Suggestion from "@tiptap/suggestion";
import { CustomComponent } from "../lib/tiptap_custom_component";
import { RawHtmlBlock } from "../lib/tiptap_raw_html_block";
import ace from "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/mode-html";

function createLiquidSuggestion(variables) {
  return Extension.create({
    name: "liquidSuggestion",

    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          char: "{{",
          items: ({ query }) =>
            variables.filter((v) =>
              v.label.toLowerCase().includes(query.toLowerCase()),
            ),
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent(props.value)
              .run();
          },
          render: () => {
            let dropdown;
            let selectedIndex = 0;
            let items = [];

            let commandFn;

            return {
              onStart(props) {
                commandFn = props.command;
                dropdown = document.createElement("div");
                dropdown.className = "tiptap-variable-dropdown";
                document.body.appendChild(dropdown);
                items = props.items;
                selectedIndex = 0;
                updateDropdown(dropdown, items, selectedIndex, commandFn);
                positionDropdown(dropdown, props);
              },

              onUpdate(props) {
                commandFn = props.command;
                items = props.items;
                selectedIndex = 0;
                updateDropdown(dropdown, items, selectedIndex, commandFn);
                positionDropdown(dropdown, props);
              },

              onKeyDown({ event }) {
                if (event.key === "ArrowDown") {
                  selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
                  updateSelection(dropdown, selectedIndex);
                  return true;
                }
                if (event.key === "ArrowUp") {
                  selectedIndex = Math.max(selectedIndex - 1, 0);
                  updateSelection(dropdown, selectedIndex);
                  return true;
                }
                if (event.key === "Enter" || event.key === "Tab") {
                  if (items[selectedIndex]) {
                    commandFn(items[selectedIndex]);
                    return true;
                  }
                }
                if (event.key === "Escape") {
                  return true;
                }
                return false;
              },

              onExit() {
                dropdown?.remove();
                dropdown = null;
              },
            };
          },
        }),
      ];
    },
  });
}

function updateDropdown(dropdown, items, selectedIndex, commandFn) {
  if (!items.length) {
    dropdown.hidden = true;
    return;
  }
  dropdown.hidden = false;
  dropdown.replaceChildren();
  items.forEach((item, i) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tiptap-variable-item";
    if (i === selectedIndex) btn.classList.add("selected");
    btn.textContent = item.label;
    btn.addEventListener("mousedown", (e) => {
      e.preventDefault();
      commandFn(item);
    });
    dropdown.appendChild(btn);
  });
}

function updateSelection(dropdown, selectedIndex) {
  dropdown.querySelectorAll(".tiptap-variable-item").forEach((btn, i) => {
    btn.classList.toggle("selected", i === selectedIndex);
    if (i === selectedIndex) btn.scrollIntoView({ block: "nearest" });
  });
}

function positionDropdown(dropdown, props) {
  const rect = props.clientRect?.();
  if (!rect) return;
  dropdown.style.position = "fixed";
  dropdown.style.left = `${rect.left}px`;
  dropdown.style.top = `${rect.bottom + 4}px`;
}

export default class extends Controller {
  static targets = [
    "editor",
    "input",
    "toolbar",
    "rawEditor",
    "rawToggle",
    "rawToggleLabel",
    "richToolbar",
  ];
  static values = {
    variables: { type: Array, default: [] },
    plainText: { type: Boolean, default: false },
  };

  connect() {
    if (!this.hasInputTarget || !this.hasEditorTarget) return;
    this.isRawMode = false;
    this.aceEditor = null;
    this.initEditor();
    this.morphHandler = () => this.reinitialize();
    document.addEventListener("turbo:morph", this.morphHandler);
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.morphHandler);
    this.destroyEditor();
    if (this.aceEditor) {
      this.aceEditor.destroy();
      this.aceEditor = null;
    }
  }

  reinitialize() {
    this.destroyEditor();
    requestAnimationFrame(() => this.initEditor());
  }

  initEditor() {
    const inputTarget = this.inputTarget;

    const SubmitOnCmdEnter = Extension.create({
      name: "submitOnCmdEnter",
      addKeyboardShortcuts() {
        return {
          "Mod-Enter": () => {
            const form = inputTarget.form;
            if (form) form.requestSubmit();
            return true;
          },
        };
      },
    });

    const isPlainText = this.plainTextValue;

    const extensions = isPlainText
      ? [
          StarterKit.configure({
            bold: false,
            italic: false,
            strike: false,
            code: false,
            codeBlock: false,
            heading: false,
            bulletList: false,
            orderedList: false,
            listItem: false,
            blockquote: false,
            horizontalRule: false,
          }),
          Placeholder.configure({
            placeholder:
              this.editorTarget.dataset.placeholder || "Type your message...",
          }),
          SubmitOnCmdEnter,
        ]
      : [
          StarterKit.configure({
            link: {
              openOnClick: false,
              HTMLAttributes: {
                rel: "noopener noreferrer nofollow",
              },
            },
          }),
          CustomComponent,
          RawHtmlBlock,
          Placeholder.configure({
            placeholder:
              this.editorTarget.dataset.placeholder || "Type your message...",
          }),
          SubmitOnCmdEnter,
        ];

    if (this.variablesValue.length) {
      extensions.push(createLiquidSuggestion(this.variablesValue));
    }

    this.editor = new Editor({
      element: this.editorTarget,
      extensions,
      content: isPlainText
        ? this.preparePlainTextContent(this.inputTarget.value)
        : this.prepareContent(this.inputTarget.value),
      onUpdate: ({ editor }) => {
        this.inputTarget.value = isPlainText
          ? this.getPlainText(editor)
          : editor.getHTML();
        this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
      },
    });

    this.updateToolbarState();
    this.editor.on("selectionUpdate", () => this.updateToolbarState());
    this.editor.on("transaction", () => this.updateToolbarState());

    // Click on empty space in the editor area to focus
    this.editorTarget.addEventListener("click", (e) => {
      if (e.target === this.editorTarget) {
        this.editor.commands.focus("end");
      }
    });

    // Make editor tabbable
    const proseMirror = this.editorTarget.querySelector("[contenteditable]");
    if (proseMirror) {
      const tabindex = this.editorTarget.dataset.tabindex || "2";
      proseMirror.setAttribute("tabindex", tabindex);
    }
  }

  destroyEditor() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run();
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run();
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run();
  }

  toggleHeading() {
    this.editor.chain().focus().toggleHeading({ level: 3 }).run();
  }

  toggleLink() {
    if (this.editor.isActive("link")) {
      this.editor.chain().focus().unsetLink().run();
      return;
    }

    const url = prompt("Enter URL:");
    if (
      url &&
      (/^(https?:\/\/|mailto:)/i.test(url) || /\{\{.*?\}\}/.test(url))
    ) {
      this.editor.chain().focus().setLink({ href: url }).run();
    }
  }

  insertComponent() {
    const tag = prompt("Component name (e.g. event-banner):");
    if (!tag) return;

    const name = tag.trim().toLowerCase();

    // Validate web component naming convention
    if (!/^[a-z][a-z0-9]*(-[a-z0-9]+)+$/.test(name)) {
      alert(
        "Invalid component name. Must start with a letter, contain a hyphen, and use only lowercase letters, numbers, and hyphens.",
      );
      return;
    }

    this.editor
      .chain()
      .focus()
      .insertContent({
        type: "customComponent",
        attrs: { tagName: name, rawAttributes: {}, innerHTML: "" },
      })
      .run();
  }

  async toggleRaw() {
    this.isRawMode = !this.isRawMode;

    if (this.isRawMode) {
      const formatted = this.formatHtml(this.inputTarget.value);
      this.editorTarget.classList.add("hidden");
      if (this.hasRawEditorTarget) {
        this.rawEditorTarget.classList.remove("hidden");
        this.initAceEditor(formatted);
      }
      if (this.hasRichToolbarTarget) {
        this.richToolbarTarget.classList.add("hidden");
      }
      if (this.hasRawToggleTarget) {
        this.rawToggleTarget.classList.add("active");
      }
      if (this.hasRawToggleLabelTarget) {
        this.rawToggleLabelTarget.textContent = "Return to visual editor";
      }
    } else {
      // Switch to rich: load Ace content into Tiptap
      const rawValue = this.aceEditor
        ? this.aceEditor.getSession().getValue()
        : this.inputTarget.value;
      this.inputTarget.value = rawValue;
      this.editor.commands.setContent(this.prepareContent(rawValue));
      if (this.hasRawEditorTarget) {
        this.rawEditorTarget.classList.add("hidden");
      }
      if (this.aceEditor) {
        this.aceEditor.destroy();
        this.aceEditor = null;
      }
      this.editorTarget.classList.remove("hidden");
      if (this.hasRichToolbarTarget) {
        this.richToolbarTarget.classList.remove("hidden");
      }
      if (this.hasRawToggleTarget) {
        this.rawToggleTarget.classList.remove("active");
      }
      if (this.hasRawToggleLabelTarget) {
        this.rawToggleLabelTarget.textContent = "Edit raw HTML";
      }
    }
  }

  initAceEditor(content) {
    this.aceEditor = ace.edit(this.rawEditorTarget);
    this.aceEditor.session.setValue(content);
    this.aceEditor.session.setMode("ace/mode/html");
    this.aceEditor.session.setUseWorker(false);
    this.aceEditor.setOptions({
      autoScrollEditorIntoView: true,
      highlightActiveLine: false,
      showPrintMargin: false,
      tabSize: 2,
      useSoftTabs: true,
      wrap: true,
    });

    // Sync changes back to the hidden input
    this.aceEditor.session.on("change", () => {
      this.inputTarget.value = this.aceEditor.getSession().getValue();
      this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    });

    // Cmd+Enter to submit
    this.aceEditor.commands.addCommand({
      name: "submitForm",
      bindKey: { win: "Ctrl-Enter", mac: "Command-Enter" },
      exec: () => {
        const form = this.inputTarget.form;
        if (form) form.requestSubmit();
      },
    });
  }

  getPlainText(editor) {
    const html = editor.getHTML();
    return html
      .replace(/<\/p><p>/g, "\n")
      .replace(/<br\s*\/?>/g, "\n")
      .replace(/<[^>]+>/g, "");
  }

  preparePlainTextContent(value) {
    if (!value) return "";
    return `<p>${value.replace(/\n/g, "<br>")}</p>`;
  }

  formatHtml(html) {
    if (!html) return "";
    const BLOCK_TAGS =
      /^(p|div|h[1-6]|ul|ol|li|table|thead|tbody|tfoot|tr|th|td|blockquote|pre|hr|br|details|summary|dl|dt|dd|section|figure|figcaption)$/i;
    const VOID_TAGS = /^(br|hr|img|input)$/i;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const lines = [];

    const walk = (node, depth) => {
      for (const child of node.childNodes) {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent.trim();
          if (text) lines.push("  ".repeat(depth) + text);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const tag = child.tagName.toLowerCase();
          const attrs = Array.from(child.attributes)
            .map((a) => ` ${a.name}="${a.value}"`)
            .join("");

          if (VOID_TAGS.test(tag)) {
            lines.push(`${"  ".repeat(depth)}<${tag}${attrs}>`);
          } else if (BLOCK_TAGS.test(tag)) {
            lines.push(`${"  ".repeat(depth)}<${tag}${attrs}>`);
            walk(child, depth + 1);
            lines.push(`${"  ".repeat(depth)}</${tag}>`);
          } else {
            // Inline tags: serialize as-is without extra newlines
            lines.push("  ".repeat(depth) + child.outerHTML);
          }
        }
      }
    };

    walk(doc.body, 0);
    return lines.join("\n");
  }

  prepareContent(value) {
    if (!value) return "";
    if (/<[a-z][\s\S]*>/i.test(value)) {
      // Strip trailing <br> elements from paragraphs to prevent
      // accumulation on round-trips through the editor
      return value.replace(/<br\s*\/?>\s*(<\/p>)/gi, "$1");
    }
    return value
      .split(/\n\n+/)
      .map((block) => `<p>${block.replace(/\n/g, "<br>")}</p>`)
      .join("");
  }

  updateToolbarState() {
    if (!this.hasToolbarTarget) return;

    const buttons = this.toolbarTarget.querySelectorAll("[data-tiptap-action]");
    buttons.forEach((button) => {
      const action = button.dataset.tiptapAction;
      let isActive = false;

      switch (action) {
        case "bold":
          isActive = this.editor.isActive("bold");
          break;
        case "italic":
          isActive = this.editor.isActive("italic");
          break;
        case "bulletList":
          isActive = this.editor.isActive("bulletList");
          break;
        case "orderedList":
          isActive = this.editor.isActive("orderedList");
          break;
        case "heading":
          isActive = this.editor.isActive("heading", { level: 3 });
          break;
        case "link":
          isActive = this.editor.isActive("link");
          break;
      }

      button.classList.toggle("active", isActive);
    });
  }
}
